// The left-hand account list on the tablet/desktop `#s04` two-pane layout:
// every connection's accounts, grouped, with the current account
// highlighted -- lets a reader jump between accounts without returning to
// `/accounts`. Signed values, same as everywhere else.
import { Link, useLocation } from 'react-router-dom';
import type { Account, CanonicalAccount } from '@/api/hooks/useAccounts';
import { AmountText } from '@/components/AmountText';
import { groupAccountsByConnection } from './ConnectionGroup';
import { scopeHref } from '@/app/shell/scope';

type AccountPickerAccount = Account | CanonicalAccount;

function pickerAccountId(account: AccountPickerAccount): string | null {
  if (!('canonical_account_id' in account)) return account.account_id;
  if (account.canonical_account_id) return account.canonical_account_id;
  return account.reconciliation_initialization_required ? account.account_id : null;
}

export function AccountPickerPane({
  accounts,
  selectedAccountId,
  className,
}: {
  accounts: AccountPickerAccount[];
  selectedAccountId: string;
  className?: string;
}) {
  const location = useLocation();
  const groups = groupAccountsByConnection(accounts);

  return (
    <nav aria-label="Accounts" className={['text-sm', className].filter(Boolean).join(' ')} data-testid="account-picker-pane">
      {groups.map((group) => (
        <div key={group.key}>
          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            {group.institutionName}
            {group.status === 'needs_login' && <span className="text-degraded-foreground"> · needs login</span>}
          </div>
          {group.accounts.map((account) => {
            const targetAccountId = pickerAccountId(account);
            const selected = targetAccountId === selectedAccountId;
            const row = (
              <>
                <span>
                  {account.display_name} <span className="text-muted-foreground">•{account.last4 ?? '----'}</span>
                </span>
                <AmountText value={account.balance} currency={account.currency} variant="balance" balanceStatus={account.balance_status} />
              </>
            );
            if (!targetAccountId) {
              return (
                <div
                  key={account.account_id}
                  data-testid="account-picker-unavailable"
                  aria-disabled="true"
                  className="flex min-h-11 items-center justify-between gap-2 rounded-md px-3 py-2 text-muted-foreground"
                >
                  {row}
                </div>
              );
            }
            return (
              <Link
                key={account.account_id}
                to={scopeHref(`/accounts/${targetAccountId}${location.hash}`, location.search, { account: targetAccountId })}
                aria-current={selected ? 'page' : undefined}
                className={[
                  'flex min-h-11 items-center justify-between gap-2 rounded-md px-3 py-2',
                  selected ? 'bg-muted font-semibold' : 'text-foreground',
                ].join(' ')}
              >
                {row}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

export default AccountPickerPane;
